import { Autocomplete, AutocompleteProps, Box, debounce, FormGroup, Popper, PopperProps, TextField, Tooltip } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { MessageType } from '@protobuf-ts/runtime'
import { C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { AutocompleteFooterMessage, LazyHookLike } from 'components/core/autocomplete/C1AutocompleteMultipleAsync'
import { defaultIsEqual, defaultRenderLabel, defaultRenderOption, Searchable } from 'components/core/autocomplete/index'
import { C1AutocompleteStyles } from 'components/core/autocomplete/styles'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useSimpleModal } from 'lib/modals'
import { ListReturn, Status } from 'lib/rpc'
import { AllowedRefs, KnownMessageTypes, protoToRef } from 'lib/websocket-notify'
import { Filter } from 'lucide-react'
import { AppRef } from 'pbts/c1/api/app/v1/app'
import React, { FC, HTMLAttributes, ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ListHookFlags } from 'redux_toolkit/lib/createPagedListApi'

const AutocompletePopper = (props: PopperProps & { 'aria-label'?: string }): React.ReactElement => (
    <Popper {...props} role="region" />
)

export type C1AutocompleteSingleAsyncProps<T extends Searchable> = {
    onQueryChange: (string) => void
    resultList: ListReturn<T>
    initialValue?: T
    onSelected: (v: T | null) => void
    getDisplayName?: (option: T | null) => string
    renderLabel?: (option: T) => string
    customAdornment?: (item: T | null) => React.ReactElement | null
    isEqual?: (option: T, value: T) => boolean
    error?: boolean
    helperText?: string
    autoFocus?: boolean
    label?: string
    placeholder?: string
} & Partial<AutocompleteProps<T, undefined, undefined, undefined>>

export interface SingleSelectionModalProps<T, Request> extends ClosableProps {
    currentSelection: T | null
    onSelectionChange: (selection: T | null) => void
    initialRequest: Request
}

export const C1AutocompleteSingleAsync = <T extends Searchable>({
    onQueryChange,
    resultList,
    initialValue,
    onSelected,
    customAdornment,
    isEqual = defaultIsEqual,
    renderLabel = defaultRenderLabel,
    renderOption = defaultRenderOption,
    error,
    helperText,
    autoFocus = false,
    label,
    placeholder,
    ...rest
}: C1AutocompleteSingleAsyncProps<T>): React.ReactElement => {
    const inputId = useId()
    const helperTextId = useId()
    const liveRegionId = useId()
    const intl = useIntl()
    const [value, setValue] = useState<T | null>(null)
    const [inputValue, setInputValue] = useState<string | null>(null)
    const [options, setOptions] = useState<T[]>([])

    const onQueryChangeDebounced = React.useMemo(
        () =>
            debounce((query: string) => {
                onQueryChange(query)
            }, 400),
        [onQueryChange],
    )

    const filterOptions = useCallback(
        (val: T, optionsList: T[]): T[] => {
            return optionsList.some((v) => isEqual(v, val)) ? optionsList : [val, ...optionsList]
        },
        [isEqual],
    )

    useEffect(() => {
        if (resultList.status === Status.OK) {
            if (value) {
                setOptions(filterOptions(value, resultList.items))
            } else {
                setOptions(resultList.items)
            }
        }
    }, [filterOptions, resultList, value])

    useEffect(() => {
        if (initialValue) {
            setValue(initialValue)
        }
    }, [initialValue])

    if (resultList.status === Status.ERROR) {
        return (
            <>
                <Box role="alert">
                    <APIError
                        err={resultList.error}
                        title={
                            <FormattedMessage
                                id="component_c1_autocomplete_search_error"
                                defaultMessage="Error: Searching"
                            />
                        }
                    />
                </Box>
                <TextField
                    disabled
                    placeholder={placeholder}
                    id={inputId}
                    aria-invalid="true"
                />
            </>
        )
    }

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Live region for screen reader announcements */}
            <Box
                component="span"
                id={liveRegionId}
                sx={{ ...visuallyHidden }}
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {resultList.status === Status.LOADING
                    ? intl.formatMessage({ id: 'component_c1_autocomplete_loading', defaultMessage: 'Loading options' })
                    : intl.formatMessage(
                        { id: 'component_c1_autocomplete_options_count', defaultMessage: '{count} options available' },
                        { count: options.length },
                    )}
            </Box>
            <Autocomplete<T, undefined, undefined, undefined>
                fullWidth
                filterOptions={(x): T[] => x}
                clearIcon={false}
                value={value}
                options={options}
                loading={resultList.status === Status.LOADING}
                getOptionLabel={renderLabel}
                isOptionEqualToValue={isEqual}
                renderOption={renderOption}
                renderInput={(params): ReactElement => {
                    return (<>
                        {label && <C1FormLabel label={label} htmlFor={inputId} />}
                        <TextField
                            name="autocomplete"
                            {...params}
                            id={inputId}
                            autoFocus={autoFocus}
                            fullWidth
                            error={error}
                            helperText={helperText}
                            placeholder={placeholder}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:focus-within': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '2px',
                                    },
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                type: 'autocomplete',
                                'aria-busy': resultList.status === Status.LOADING,
                                'aria-invalid': error,
                                'aria-describedby': helperText ? helperTextId : undefined,
                                startAdornment: customAdornment && customAdornment(value),
                                endAdornment: (
                                    <>
                                        {resultList.status === Status.LOADING && <C1CircularProgress color="inherit" />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            FormHelperTextProps={{
                                id: helperTextId,
                            }}
                        />
                    </>)
                }}
                onBlur={(): void => {
                    if (inputValue && inputValue.length > 0) {
                        // Clear out filtered results as we will get full results on next focus
                        setOptions([])
                    }
                    onQueryChangeDebounced.clear()
                }}
                onFocus={(): void => {
                    if (inputValue === null || inputValue.length > 0) {
                        // Get full list only if first time load (null) or if previously filtered(len > 0)
                        // We've already loaded the full list otherwise
                        onQueryChange('')
                    }
                    // Optimization to clear out just the input value, so we won't fetch full list again
                    // The autocomplete value still stays the same, and UI still shows the previously selected value
                    setInputValue('')
                }}
                onInputChange={(event, newInputValue, reason): void => {
                    // If there's a default value, don't call setInputValue.
                    if (reason === 'reset') {
                        return
                    }
                    setInputValue(newInputValue)
                    onQueryChangeDebounced(newInputValue)
                }}
                onChange={(e, val: T | null): void => {
                    if (initialValue && val === null) {
                        return
                    }
                    setOptions(val ? filterOptions(val, options) : options)
                    setValue(val)
                    onSelected(val)
                }}
                PopperComponent={(props): React.ReactElement => (
                    <AutocompletePopper
                        {...props}
                        aria-label={intl.formatMessage({ id: 'autocomplete_search_results', defaultMessage: 'Search results' })}
                    />
                )}
                {...rest}
            />
        </Box>
    )
}

export type Requestable<R extends AllowedRefs> = { query: string, expandMask?: { paths: string[] }, refs: Array<R> }
export type NonRequestable = { query: string, expandMask?: { paths: string[] }, refs?: never }
export type AutocompleteListboxProps = HTMLAttributes<HTMLUListElement> & {
    canLoadMore?: boolean;
    autocompleteFooterMessage?: React.ReactElement;
}

type C1AutocompleteSingleAsyncHookProps<T extends Searchable, Request extends NonRequestable | Requestable<AllowedRefs>> = {
    Req: { create: (q: Partial<Request>) => Request }
    reqExpandMask?: { paths: string[] }
    hook: LazyHookLike<Request, T>
    type: KnownMessageTypes
    value?: T | null
    onSelected: (v: T | null, reason?: 'reset' | 'clear' | 'selected') => void
    getDisplayName?: (option: T | null) => string
    renderLabel?: (option: T) => string
    helperText?: string
    autoFocus?: boolean
    label?: string
    placeholder?: string
    loading?: boolean
    error?: boolean
    initialRef?: Request extends Requestable<infer R> ? R extends AllowedRefs ? R : never : never
    initialValue?: T
    reqOptions?: Partial<Request>
    popperStyleProps?: PopperProps['style']
    ListboxComponent?: React.ComponentType<AutocompleteListboxProps>
    listHookFlags?: ListHookFlags
    noOptionsText?: React.ReactNode
    SingleSelectionModal?: FC<SingleSelectionModalProps<T, Request>>
} & Partial<AutocompleteProps<T, undefined, true | undefined, undefined>>

const isEqual_ = <T extends object>(a: T, b: T, type: KnownMessageTypes): boolean => {
    /*
        NOTE: this is probably not an AppRef, but we can't discriminate the union into the correct types because
        many are indistinguishable (AppRef vs UserRef).  Correlating T with its associated MessageType<$Ref>
        via TS ternaries doesn't help, either.  Its better to just step around the type system.

        NOTE: Intropecting the types is practically difficult.
        The typenames are really easy to drop becuase they are non-enumerable,
        ie, lost when you do a splat expansion...

        this import is really hard to find, please leave this comment here
        import { MESSAGE_TYPE } from '@protobuf-ts/runtime'
     */
    const refType = protoToRef(type) as unknown as MessageType<AppRef>
    if (!refType) {
        return false
    }
    const r1 = refType.create(a)
    const r2 = refType.create(b)

    return refType.equals(r1, r2)
}

// A controlled/uncontrolled fetcher & autocompleter
export const C1AutocompleteSingleAsyncHook = <Request extends NonRequestable | Requestable<AllowedRefs>, T extends Searchable>({
    renderLabel = defaultRenderLabel,
    renderOption = defaultRenderOption,
    ...rest
}: C1AutocompleteSingleAsyncHookProps<T, Request>): React.ReactElement => {
    const {
        Req,
        reqExpandMask,
        initialRef,
        initialValue,
        helperText,
        autoFocus,
        label,
        placeholder,
        type,
        value,
        onSelected,
        hook: useLazyHook,
        loading: parentIsLoading,
        error: parentIsError,
        reqOptions,
        disabled,
        popperStyleProps,
        isOptionEqualToValue,
        ListboxComponent,
        listHookFlags,
        noOptionsText,
        SingleSelectionModal,
        fullWidth = true,
        ...autocompleteProps
    } = rest
    const { open: openModal } = useSimpleModal()
    const inputId = useId()
    const helperTextId = useId()
    const liveRegionId = useId()
    const [isModalOpen, setIsModalOpen] = useState(false)

    if (process.env.NODE_ENV === 'development') {
        const wasControlled = useRef<boolean>(value !== undefined) // eslint-disable-line react-hooks/rules-of-hooks
        const isControlled = value !== undefined
        if (isControlled !== wasControlled.current) {
            throw new Error(`Illegal: don't change a controlled component to uncontrolled or vice versa: ${wasControlled} -> ${isControlled}`)
        }
        if ((initialRef || initialValue) && value !== undefined) {
            throw new Error(`Illegal: don't mix initial values/refs && values ${initialValue}/${initialRef} && ${value}`)
        }
    }

    const intl = useIntl()
    const isEqual = useCallback((a, b) => isEqual_(a, b, type), [type])

    // _value is used in the case this is uncontrolled
    const [_value, setValue] = useState<T | null>(null)
    const v = value === undefined ? _value : value

    const [inputValue, setInputValue] = useState<string | null>(null)
    const [query, setQuery] = useState<string | null>(null)
    const onQueryChangeDebounced = React.useMemo(() => debounce(setQuery, 400), [setQuery])

    const handleModalOpen = useCallback(() => {
        if (!SingleSelectionModal) return

        // Create initial request for the modal
        const initialRequest = Req.create(reqOptions || {})

        setIsModalOpen(true)
        openModal(SingleSelectionModal, {
            currentSelection: v,
            onSelectionChange: (selection: T | null): void => {
                setValue(selection)
                onSelected(selection, 'selected')
                setIsModalOpen(false)
            },
            initialRequest,
        })()
    }, [SingleSelectionModal, openModal, v, onSelected, Req, reqOptions])

    const filterOptions = useCallback(
        (val: T, optionsList: T[]): Array<T> => {
            return optionsList.some((o) => (isOptionEqualToValue
                ? isOptionEqualToValue(o, val)
                : isEqual(o, val))) ? optionsList : [val, ...optionsList]
        },
        [isEqual, isOptionEqualToValue],
    )

    const previousValue = useRef(value)
    // Hidden API: setting the value to _null_ is a hard reset
    const hardReset = value === null && previousValue.current !== value
    previousValue.current = value
    useEffect(() => {
        if (!hardReset) {
            return
        }
        setInputValue('')
        setQuery('')
        onSelected(null, 'reset')
        // eslint-disable-next-line react-hooks/exhaustive-deps  -- don't trigger on onSelected
    }, [hardReset])

    // MUI requires that the value must be in the options list
    const [options, setOptions] = useState<Array<T>>(() => (initialValue ? filterOptions(initialValue, []) : []))
    useEffect(() => {
        if (!v) {
            return
        }
        setOptions((o) => (v ? filterOptions(v, o) : o))
    }, [v, setOptions, filterOptions])

    const { trigger, data, isError, isLoading, error, canLoadMore } = useLazyHook()
    const loading = parentIsLoading || isLoading

    useEffect(() => {
        if (query === null || disabled) {
            return
        }
        const req = Req.create({
            query: query || '',
            expandMask: reqExpandMask,
            ...reqOptions,
        } as Request)
        trigger(req, listHookFlags)
    }, [query, Req, reqExpandMask, disabled]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { data?.length && setOptions(data.flat()) }, [data])

    const initialValueStateMachine = useRef(false)
    useEffect(() => {
        if (!initialValue || parentIsLoading || initialValueStateMachine.current) {
            return
        }
        initialValueStateMachine.current = true
        setOptions(filterOptions(initialValue, options))
        setValue(initialValue)
    }, [parentIsLoading, initialValue]) // eslint-disable-line react-hooks/exhaustive-deps

    // One shot initial value.
    // 0 -> initial
    // 1 -> triggered
    // 2 -> got data back
    const initialRefStateMachine = useRef(0)
    useEffect(() => {
        if (!initialRef || parentIsLoading || initialRefStateMachine.current === 2 || initialValue) {
            return
        }

        if (initialRefStateMachine.current === 0) {
            const arg = { expandMask: reqExpandMask, refs: [initialRef] } as Request
            const req = Req.create(arg)
            const { request: didStuff } = trigger(req)
            if (didStuff) {
                initialRefStateMachine.current = 1
            }
            return
        }

        if (!data?.length) {
            return
        }

        initialRefStateMachine.current = 2
        for (const d of data.flat()) {
            if (isEqual(d, initialRef)) {
                setValue(d)
                onSelected(d)
            }
        }
    }, [parentIsLoading, initialRef, data, trigger, Req]) // eslint-disable-line react-hooks/exhaustive-deps

    const ListboxWithFooter = useMemo(() =>
        React.forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(
            (props, ref) => <ul ref={ref} {...props}>
                {props.children}
                {canLoadMore && <AutocompleteFooterMessage />}
            </ul>,
        ), [canLoadMore])

    if (isError) {
        return <>
            <Box role="alert">
                <APIError
                    err={error}
                    title={
                        <FormattedMessage
                            id="component_c1_autocomplete_search_error"
                            defaultMessage="Error: Searching"
                        />
                    }
                />
            </Box>
            <TextField
                disabled
                placeholder={placeholder}
                id={inputId}
                aria-invalid="true"
            />
        </>
    }

    return <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'fit-content' }}>
        {/* Live region for screen reader announcements */}
        <Box
            component="span"
            id={liveRegionId}
            sx={{ ...visuallyHidden }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
        >
            {loading
                ? intl.formatMessage({ id: 'component_c1_autocomplete_loading', defaultMessage: 'Loading options' })
                : intl.formatMessage(
                    { id: 'component_c1_autocomplete_options_count', defaultMessage: '{count} options available' },
                    { count: options.length },
                )}
        </Box>
        <Autocomplete<T, undefined, undefined | true, undefined>
            fullWidth
            disabled={disabled || (parentIsLoading && !initialValueStateMachine.current)}
            loading={loading}
            filterOptions={(x): T[] => x}
            value={v}
            options={options}
            getOptionLabel={renderLabel}
            isOptionEqualToValue={isOptionEqualToValue || isEqual}
            renderOption={renderOption}
            noOptionsText={<>
                {loading
                    ? rest.loadingText || '\u2026'
                    : inputValue
                        ? intl.formatMessage(
                            {
                                id: 'autocomplete_no_options_for_search_query_text',
                                defaultMessage: '(No available options for "{search_query}")',
                            },
                            { search_query: inputValue },
                        )
                        : intl.formatMessage(
                            {
                                id: 'autocomplete_no_options',
                                defaultMessage: '(No available options)',
                            },
                        )}
                {noOptionsText}
            </>
            }
            renderInput={(params): ReactElement => <FormGroup>
                {label && <C1FormLabel label={label} htmlFor={inputId} disabled={disabled} />}
                <TextField
                    name="autocomplete"
                    {...params}
                    id={inputId}
                    autoFocus={autoFocus}
                    fullWidth
                    error={parentIsError}
                    helperText={helperText}
                    placeholder={placeholder}
                    size="small"
                    sx={{
                        ...C1AutocompleteStyles.textField,
                        '& .MuiOutlinedInput-root': {
                            '&:focus-within': {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: '2px',
                            },
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        type: 'autocomplete',
                        'aria-busy': loading,
                        'aria-invalid': parentIsError,
                        'aria-describedby': helperText ? helperTextId : undefined,
                        endAdornment: (
                            <>
                                {loading && <C1CircularProgress color="inherit" />}
                                {SingleSelectionModal && !disabled && (
                                    <Tooltip title={<FormattedMessage id="component_c1_autocomplete_advanced_search" defaultMessage="Show filters" />}>
                                        <C1IconButton
                                            size="small"
                                            onClick={handleModalOpen}
                                            sx={{
                                                mr: 1,
                                                '&:focus-visible': {
                                                    outline: '2px solid',
                                                    outlineColor: 'primary.main',
                                                    outlineOffset: '2px',
                                                },
                                            }}
                                            aria-label={intl.formatMessage({ id: 'component_c1_autocomplete_advanced_search', defaultMessage: 'Show filters' })}
                                            aria-expanded={isModalOpen}
                                        >
                                            <C1Icon icon={Filter} />
                                        </C1IconButton>
                                    </Tooltip>
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    FormHelperTextProps={{
                        id: helperTextId,
                    }}
                />
            </FormGroup>}
            onBlur={(): void => onQueryChangeDebounced.clear()}
            onOpen={(): void => void (query === null && setQuery(''))}
            onInputChange={(_event, newInputValue, reason): void => {
                if (reason === 'clear') {
                    setInputValue('')
                    onSelected(null, 'clear')
                    setValue(null)
                    onQueryChangeDebounced(newInputValue)
                    return
                }
                if (reason === 'reset' && options.length) {
                    // leave existing input and terms up....
                    return
                }
                setInputValue(newInputValue)
                onQueryChangeDebounced(newInputValue)
            }}
            onChange={(_, val: T | null): void => {
                if (initialRef && val === null) {
                    return
                }
                setValue(val)
                onSelected(val, 'selected')
            }}
            ListboxComponent={ListboxComponent || ListboxWithFooter}
            ListboxProps={{
                canLoadMore,
                autocompleteFooterMessage: <AutocompleteFooterMessage />,
            } as AutocompleteListboxProps}
            PopperComponent={(props): ReactElement => (
                <AutocompletePopper
                    {...props}
                    style={popperStyleProps ? { ...popperStyleProps } : props.style}
                    placement={popperStyleProps ? 'bottom-start' : props.placement}
                    aria-label={intl.formatMessage({ id: 'autocomplete_search_results', defaultMessage: 'Search results' })}
                />
            )}
            {...autocompleteProps}
        />
    </Box>
}
